import { useEffect, useRef, useState } from "react";
import feature1Img from "../../../assets/readdy/feature-1.jpg";
import feature2Img from "../../../assets/readdy/feature-2.jpg";
import feature3Img from "../../../assets/readdy/feature-3.jpg";
import feature4Img from "../../../assets/readdy/feature-4.jpg";

const features = [
  {
    icon: "ri-code-s-slash-line",
    title: "Custom Software Development",
    desc: "We build tailor-made software solutions from the ground up — scalable, secure, and engineered to solve your exact business challenges.",
    tag: "Development",
    img: feature1Img,
  },
  {
    icon: "ri-global-line",
    title: "Web Application Development",
    desc: "From sleek marketing sites to complex SaaS platforms — we craft performant, modern web apps built to scale with your business.",
    tag: "Web Apps",
    img: feature2Img,
  },
  {
    icon: "ri-smartphone-line",
    title: "Mobile App Development",
    desc: "Native and cross-platform mobile apps for iOS and Android — beautiful, fast, and built with the user experience at the core.",
    tag: "Mobile",
    img: feature3Img,
  },
  {
    icon: "ri-cloud-line",
    title: "Cloud & DevOps Solutions",
    desc: "We architect and deploy robust cloud infrastructure on AWS, GCP, and Azure — with CI/CD pipelines, monitoring, and auto-scaling built in.",
    tag: "Cloud",
    img: feature4Img,
  },
];

export default function Features() {
  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="py-24 md:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`mb-16 md:mb-20 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4 block">What We Do</span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-950 leading-tight max-w-xl">
              Services Built<br />
              <span className="italic font-light text-gray-400">for modern businesses</span>
            </h2>
            <p className="text-gray-500 max-w-sm text-base leading-relaxed">
              Everything you need to launch and scale a world-class digital product — we handle it all, end to end.
            </p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Feature List */}
          <div className="flex flex-col gap-3">
            {features.map((f, i) => (
              <button
                key={f.title}
                onClick={() => setActive(i)}
                className={`group text-left w-full px-6 py-5 rounded-2xl border transition-all duration-300 cursor-pointer ${
                  active === i
                    ? "bg-gray-950 border-gray-950 text-white"
                    : "bg-white border-gray-100 hover:border-gray-200 text-gray-800"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 transition-colors ${active === i ? "bg-white/10" : "bg-gray-50"}`}>
                    <i className={`${f.icon} text-xl ${active === i ? "text-white" : "text-gray-600"}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-base">{f.title}</span>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ml-2 ${active === i ? "bg-white/15 text-white/70" : "bg-gray-100 text-gray-500"}`}>{f.tag}</span>
                    </div>
                    <p className={`text-sm mt-1.5 leading-relaxed ${active === i ? "text-white/60" : "text-gray-500"}`}>{f.desc}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Feature Image */}
          <div className="relative overflow-hidden rounded-2xl h-80 lg:h-auto min-h-[320px]">
            {features.map((f, i) => (
              <img
                key={f.title}
                src={f.img}
                alt={f.title}
                className={`absolute inset-0 w-full h-full object-cover object-top transition-all duration-500 ${active === i ? "opacity-100 scale-100" : "opacity-0 scale-105"}`}
              />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
            <div className="absolute bottom-6 left-6">
              <span className="text-white font-semibold text-lg">{features[active].title}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
